// ----- Phase 5.X-screen-review-part-b (toolbar) — CompilerToolbar -----
//
// Sits above the DocumentList in the CompilerPage middle pane, matching
// V1 ReviewScreen's combined undo/redo + sequence-selector toolbar row
// (ReviewScreen.jsx ~line 1828).
//
// V1 toolbar layout (left → right):
//   [Sequences: <selector>] [doc count] | [↩ Undo] [↪ Redo] | [Validation: N/M] → [N reviewed]
//
// Part B scope — V2 implements the stateless display elements:
//   • Sequence number badge  ("Seq 0003")
//   • ValidationScoreBadge   (moved here from CompilerPage header)
//   • Visual pipe dividers between groups
//
// DEFERRED to Part C:
//   • Undo / Redo buttons  (require local document-order undo stack)
//   • Sibling-sequence selector  (requires /submissions/:id/siblings endpoint)
//
// Props:
//   submissionId    — passed through to ValidationScoreBadge; renders null when absent
//   sequenceNumber  — from submission.SequenceNumber (e.g. "0003")
//   onBadgeClick    — scrolls the ValidationStatusBanner into view (same as before)

import { useEffect, useState, type CSSProperties, type ReactElement } from 'react';
import { ValidationScoreBadge } from './ValidationScoreBadge';
import { SiblingSequenceSelector } from './SiblingSequenceSelector';
import { CompilerHelpModal } from './CompilerHelpModal';
import type { SubmissionSibling } from '@/api/v2Client';

export interface CompilerToolbarProps {
  /** The active submission ID. When null/undefined the toolbar is hidden. */
  readonly submissionId: string | null | undefined;
  /**
   * Current sequence number string from Submission.SequenceNumber
   * (e.g. "0003"). Displayed as "Seq 0003". Hidden when null/undefined.
   */
  readonly sequenceNumber: string | null | undefined;
  /** Scrolls the ValidationStatusBanner into view when the badge is clicked. */
  readonly onBadgeClick?: () => void;
  /**
   * phase-package-viewer-live-structure addendum 4 — starts the compile flow
   * (the same PublishWizard the hero card's Compile button starts). The
   * compile/generate buttons lived ONLY inside the expanded hero card, so with
   * details collapsed (the default) the compiler screen had no way to compile.
   * The primary action is now persistently reachable here; the hero card's
   * button remains — ONE flow, two triggers, no duplicate surface (the wizard
   * itself is unchanged and single).
   */
  readonly onCompileClick?: () => void;
  /**
   * Phase compiler-reviewed-count (TODO-080) — at-a-glance review progress
   * derived from the document list loaded in CompilerPage. All three counts
   * scope to the same set (typically the currently-selected section).
   * When `totalCount === 0` the indicators are hidden entirely.
   */
  readonly reviewedCount?: number;
  readonly totalCount?: number;
  readonly reclassifiedCount?: number;
  /**
   * Phase settings-continuous-validation (TODO-074) — when false, a
   * "Re-validate" button appears in the toolbar so the user can trigger
   * an on-demand validation run without scrolling to the banner.
   * When true (or undefined), continuous polling is active and the button
   * is hidden (polling keeps the badge fresh automatically).
   */
  readonly continuousValidationOn?: boolean;
  /** Called when the user clicks "Re-validate" (only shown when continuousValidationOn=false). */
  readonly onRevalidate?: () => void;
  /**
   * Phase compiler-undo-stack (TODO-057) — undo button.
   * When `canUndo` is true the button is enabled; `undoLabel` is shown as
   * the button text + tooltip. `onUndo` fires Ctrl+Z or button click.
   */
  readonly canUndo?: boolean;
  readonly undoLabel?: string;
  readonly onUndo?: () => void;
  /**
   * Phase sibling-selector-toolbar (TODO-058 Part B) — list of sibling
   * sequences (same NdaNumber + TenantId). When 2+ rows are present, the
   * SiblingSequenceSelector dropdown is rendered next to the Seq badge.
   * Empty/undefined → no selector.
   */
  readonly siblings?: readonly SubmissionSibling[];
  /** Selected sibling id (null = no overlay). */
  readonly selectedSiblingId?: string | null;
  /** Called when the user changes overlay selection. */
  readonly onSiblingChange?: (siblingId: string | null) => void;
}

const toolbarStyle: CSSProperties = {
  // Match V1: padding:"6px 14px", background:"var(--surface2)", borderRadius:8,
  // display:"flex", alignItems:"center", gap:10, flexWrap:"wrap", fontSize:12
  padding: '6px 14px',
  background: 'var(--surface2)',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
  fontSize: 12,
  marginBottom: 12,
};

const dividerStyle: CSSProperties = {
  color: 'var(--border)',
  fontSize: 14,
  userSelect: 'none',
};

const seqStyle: CSSProperties = {
  fontWeight: 600,
  color: 'var(--text)',
};

/**
 * Compiler toolbar rendered above the DocumentList in the CompilerPage
 * middle pane. Returns null when no submission is selected.
 */
export function CompilerToolbar({
  submissionId,
  sequenceNumber,
  onBadgeClick,
  reviewedCount = 0,
  totalCount = 0,
  reclassifiedCount = 0,
  continuousValidationOn,
  onRevalidate,
  onCompileClick,
  siblings,
  selectedSiblingId = null,
  onSiblingChange,
  canUndo = false,
  undoLabel,
  onUndo,
}: CompilerToolbarProps): ReactElement | null {
  // Phase compiler-help-modal — discoverability for keyboard shortcuts +
  // hidden features (undo, drag-to-reclassify, sibling sequences, lock,
  // publish wizard). "?" button toggles; Shift+? opens globally when the
  // modal is closed. Hook is unconditional (must run before the early
  // null return below) so Rules of Hooks aren't violated.
  const [helpOpen, setHelpOpen] = useState(false);
  useEffect(() => {
    if (helpOpen) return undefined;
    const handler = (e: KeyboardEvent): void => {
      // Shift+? = the "?" character on most keyboard layouts. Avoid
      // hijacking when the user is typing in an input/textarea.
      if (e.key !== '?') return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      if (tag === 'input' || tag === 'textarea' || target?.isContentEditable) {
        return;
      }
      e.preventDefault();
      setHelpOpen(true);
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [helpOpen]);

  if (!submissionId) return null;

  // Phase compiler-reviewed-count (TODO-080) — hide indicators when the
  // doc list is empty (no section selected, still loading, etc.).
  const showReviewIndicator = totalCount > 0;
  const allReviewed = totalCount > 0 && reviewedCount >= totalCount;
  const showReclassified = reclassifiedCount > 0;

  return (
    <div
      data-testid="compiler-toolbar"
      role="toolbar"
      aria-label="Compiler toolbar"
      style={toolbarStyle}
    >
      {/* Sequence number badge — Part C will expand this into a full
          sibling-sequence selector when the /submissions/:id/siblings
          endpoint is available. */}
      {sequenceNumber != null && sequenceNumber !== '' && (
        <>
          <span data-testid="compiler-toolbar-seq" style={seqStyle}>
            Seq {sequenceNumber}
          </span>
          <span aria-hidden="true" style={dividerStyle}>
            |
          </span>
        </>
      )}

      {/* Phase sibling-selector-toolbar (TODO-058 Part B). Selector hides
          itself when fewer than 2 siblings exist. */}
      {siblings && siblings.length >= 2 && onSiblingChange && (
        <>
          <SiblingSequenceSelector
            siblings={siblings}
            selectedSiblingId={selectedSiblingId}
            onChange={onSiblingChange}
          />
          <span aria-hidden="true" style={dividerStyle}>
            |
          </span>
        </>
      )}

      {/* Validation score badge — moved from CompilerPage header per
          Phase 5.X-screen-review-part-b (toolbar). Clicking scrolls the
          ValidationStatusBanner into view. */}
      <ValidationScoreBadge submissionId={submissionId} onBadgeClick={onBadgeClick} />

      {/* Phase settings-continuous-validation (TODO-074) — when continuous
          validation is OFF, show a quick Re-validate trigger next to the
          badge so the user doesn't have to scroll up to the banner button.
          Hidden when continuousValidationOn=true (polling keeps the badge
          fresh automatically). */}
      {continuousValidationOn === false && onRevalidate && (
        <button
          type="button"
          data-testid="toolbar-revalidate-btn"
          onClick={onRevalidate}
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 4,
            padding: '3px 10px',
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            color: 'var(--text)',
          }}
          aria-label="Run validation now"
        >
          Re-validate
        </button>
      )}

      {/* phase-package-viewer-live-structure addendum 4 — the persistent
          compile trigger (primary action styling; it IS the screen's primary
          action). Same flow as the hero card's Compile button. */}
      {onCompileClick && (
        <button
          type="button"
          data-testid="toolbar-compile-btn"
          onClick={onCompileClick}
          style={{
            background: 'var(--accent)',
            border: '1px solid var(--accent)',
            borderRadius: 4,
            padding: '3px 12px',
            fontSize: 12,
            fontWeight: 700,
            cursor: 'pointer',
            color: '#fff',
          }}
          aria-label="Compile this submission"
        >
          Compile…
        </button>
      )}

      {/* Phase compiler-undo-stack (TODO-057) — undo button */}
      {onUndo !== undefined && (
        <>
          <span aria-hidden="true" style={dividerStyle}>|</span>
          <button
            type="button"
            data-testid="toolbar-undo-btn"
            onClick={onUndo}
            disabled={!canUndo}
            title={canUndo && undoLabel ? `Undo: ${undoLabel}` : 'Nothing to undo'}
            aria-label="Undo last action"
            style={{
              background: 'var(--surface)',
              border: '1px solid var(--border)',
              borderRadius: 4,
              padding: '3px 10px',
              fontSize: 12,
              fontWeight: 600,
              cursor: canUndo ? 'pointer' : 'not-allowed',
              color: canUndo ? 'var(--text)' : 'var(--text-muted)',
              opacity: canUndo ? 1 : 0.5,
            }}
          >
            ↩ {canUndo && undoLabel ? undoLabel : 'Undo'}
          </button>
        </>
      )}

      {/* Phase compiler-help-modal — "?" button opens the help modal.
          Kept compact (icon-only) and visually unobtrusive; the global
          Shift+? handler is the power-user path. */}
      <button
        type="button"
        data-testid="compiler-toolbar-help-btn"
        onClick={() => setHelpOpen(true)}
        title="Compiler help (Shift + ?)"
        aria-label="Open Compiler help"
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: '50%',
          width: 22,
          height: 22,
          padding: 0,
          fontSize: 12,
          fontWeight: 700,
          cursor: 'pointer',
          color: 'var(--text-muted)',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          lineHeight: 1,
        }}
      >
        ?
      </button>

      {/* Spacer — pushes the review indicators to the right edge so they
          sit between the validation badge and the (parent-rendered) Final
          Compile button per Phase compiler-reviewed-count (TODO-080). */}
      <span style={{ flex: 1 }} />

      {/* Phase compiler-reviewed-count (TODO-080) — at-a-glance progress. */}
      {showReviewIndicator && (
        <span
          data-testid="compiler-toolbar-reviewed"
          aria-label={`${reviewedCount} of ${totalCount} documents reviewed`}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: allReviewed ? 'var(--ok, #15803d)' : 'var(--text-muted)',
          }}
        >
          {reviewedCount}/{totalCount} reviewed
        </span>
      )}
      {showReclassified && (
        <span
          data-testid="compiler-toolbar-reclassified"
          aria-label={`${reclassifiedCount} reclassified`}
          style={{
            fontSize: 11,
            fontWeight: 600,
            color: 'var(--warn, #b45309)',
          }}
        >
          {reclassifiedCount} reclassified
        </span>
      )}

      <CompilerHelpModal open={helpOpen} onClose={() => setHelpOpen(false)} />
    </div>
  );
}
